// Simple Add Recipe Form Component
import React, { useState } from 'react';
import { addRecipe } from '../services/recipeService';

const AddRecipeForm = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    meal: 'breakfast', // breakfast, lunch, snack, dinner
    calories: 0,
    protein: 0,
    ingredients: [{ name: '', quantity: '', unit: '', calories: 0, protein: 0 }],
    instructions: '',
    servings: 1,
    prepTime: '',
    cookTime: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await addRecipe(formData);
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { name: '', quantity: '', unit: '', calories: 0, protein: 0 }]
    });
  };

  const updateIngredient = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    
    // Recalculate totals from ingredients
    const totalCalories = newIngredients.reduce((sum, ing) => sum + (parseFloat(ing.calories) || 0), 0);
    const totalProtein = newIngredients.reduce((sum, ing) => sum + (parseFloat(ing.protein) || 0), 0);
    
    setFormData({ 
      ...formData, 
      ingredients: newIngredients,
      calories: Math.round(totalCalories),
      protein: Math.round(totalProtein * 10) / 10 // Round to 1 decimal place
    });
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length > 1) {
      const newIngredients = formData.ingredients.filter((_, i) => i !== index);
      
      // Recalculate totals after removing ingredient
      const totalCalories = newIngredients.reduce((sum, ing) => sum + (parseFloat(ing.calories) || 0), 0);
      const totalProtein = newIngredients.reduce((sum, ing) => sum + (parseFloat(ing.protein) || 0), 0);
      
      setFormData({ 
        ...formData, 
        ingredients: newIngredients,
        calories: Math.round(totalCalories),
        protein: Math.round(totalProtein * 10) / 10
      });
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Add New Recipe</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>
        
        <form onSubmit={handleSubmit} className="recipe-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label>Recipe Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Meal Type</label>
            <select
              value={formData.meal}
              onChange={(e) => setFormData({ ...formData, meal: e.target.value })}
              required
            >
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="snack">Snack</option>
              <option value="dinner">Dinner</option>
            </select>
          </div>

          <div className="form-group">
            <label>Ingredients</label>
            <div className="ingredient-headers" style={{ display: 'flex', gap: '5px', marginBottom: '5px', fontSize: '12px', color: '#666' }}>
              <span style={{ flex: '2' }}>Name</span>
              <span style={{ flex: '1' }}>Quantity</span>
              <span style={{ flex: '1' }}>Unit</span>
              <span style={{ flex: '1' }}>Calories</span>
              <span style={{ flex: '1' }}>Protein (g)</span>
              <span style={{ flex: '0 0 auto', width: '30px' }}></span>
            </div>
            {formData.ingredients.map((ingredient, index) => (
              <div key={index} className="ingredient-row" style={{ display: 'flex', gap: '5px', marginBottom: '5px', alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Ingredient name"
                  value={ingredient.name}
                  onChange={(e) => updateIngredient(index, 'name', e.target.value)}
                  required
                  style={{ flex: '2' }}
                />
                <input
                  type="text"
                  placeholder="Qty"
                  value={ingredient.quantity}
                  onChange={(e) => updateIngredient(index, 'quantity', e.target.value)}
                  required
                  style={{ flex: '1' }}
                />
                <input
                  type="text"
                  placeholder="Unit"
                  value={ingredient.unit}
                  onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                  required
                  style={{ flex: '1' }}
                />
                <input
                  type="number"
                  placeholder="Cal"
                  min="0"
                  value={ingredient.calories || ''}
                  onChange={(e) => updateIngredient(index, 'calories', parseFloat(e.target.value) || 0)}
                  title="Calories for this ingredient"
                  style={{ flex: '1' }}
                />
                <input
                  type="number"
                  placeholder="Protein"
                  min="0"
                  step="0.1"
                  value={ingredient.protein || ''}
                  onChange={(e) => updateIngredient(index, 'protein', parseFloat(e.target.value) || 0)}
                  title="Protein (g) for this ingredient"
                  style={{ flex: '1' }}
                />
                <button
                  type="button"
                  onClick={() => removeIngredient(index)}
                  className="remove-btn"
                  disabled={formData.ingredients.length === 1}
                  style={{ flex: '0 0 auto' }}
                >
                  −
                </button>
              </div>
            ))}
            <button type="button" onClick={addIngredient} className="add-ingredient-btn">
              + Add Ingredient
            </button>
            
            {/* Nutrition Summary */}
            <div className="nutrition-summary" style={{ 
              marginTop: '15px', 
              padding: '10px', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '5px', 
              border: '1px solid #dee2e6',
              fontSize: '14px'
            }}>
              <strong>Recipe Totals:</strong> {formData.calories} calories, {formData.protein}g protein
              {formData.servings > 1 && (
                <span style={{ color: '#666', marginLeft: '10px' }}>
                  (Per serving: {Math.round(formData.calories / formData.servings)} cal, {Math.round((formData.protein / formData.servings) * 10) / 10}g protein)
                </span>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Servings</label>
              <input
                type="number"
                min="1"
                value={formData.servings}
                onChange={(e) => setFormData({ ...formData, servings: parseInt(e.target.value) })}
                required
              />
            </div>
            <div className="form-group">
              <label>Prep Time</label>
              <input
                type="text"
                placeholder="e.g., 15 minutes"
                value={formData.prepTime}
                onChange={(e) => setFormData({ ...formData, prepTime: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Cook Time</label>
              <input
                type="text"
                placeholder="e.g., 30 minutes"
                value={formData.cookTime}
                onChange={(e) => setFormData({ ...formData, cookTime: e.target.value })}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Instructions</label>
            <textarea
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              rows={4}
              placeholder="Step by step instructions..."
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Adding...' : 'Add Recipe'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddRecipeForm;